import Icon from "@/v2-ui/icon"
import type { IconNames } from "@/v2-ui/icon/icon.types"
import Popover from "@/v2-ui/popover"
import cn from "@/v2-ui/utils/utils.cn"

export type LicenseFeatureAccessAboutPoint = {
  key: string
  icon: IconNames
  lead: string
  text: string
}

export type LicenseFeatureAccessAboutProps = {
  /** The one sentence always in view. */
  lead: string
  /** The three things a first-time reader must know, behind "How it works". */
  points: readonly LicenseFeatureAccessAboutPoint[]
  className?: string
}

// The board's explainer, one line tall: the sentence every reader needs,
// and the longer version behind a click for whoever meets the word
// "capability" for the first time. It used to be three columns of prose
// above the rows, which cost a screen of table on every visit for a text
// most operators had read once.
function LicenseFeatureAccessAbout(props: LicenseFeatureAccessAboutProps) {
  const { lead, points, className } = props

  return (
    <p
      className={cn(
        "flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground",
        className,
      )}
    >
      <Icon src="info" size={13} className="shrink-0 text-primary" />
      <span>{lead}</span>
      <Popover>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="font-medium text-foreground underline-offset-2 hover:underline"
            data-feature-code="UI_BackofficeWhatsNew_About_Open"
          >
            How it works
          </button>
        </Popover.Trigger>
        <Popover.Content
          size="fit"
          align="start"
          sideOffset={6}
          className="flex w-[28rem] max-w-[var(--radix-popover-content-available-width)] flex-col gap-3 text-xs"
        >
          {points.map((point) => (
            <p key={point.key} className="flex gap-2 text-muted-foreground">
              <Icon
                src={point.icon}
                size={14}
                className="mt-0.5 shrink-0 text-primary"
              />
              <span>
                <span className="font-medium text-foreground">
                  {point.lead}
                </span>
                : {point.text}
              </span>
            </p>
          ))}
        </Popover.Content>
      </Popover>
    </p>
  )
}

export default LicenseFeatureAccessAbout
